import React, { useCallback, type KeyboardEvent, type MouseEvent } from 'react';
import { ColorPaletteMenu, Compact } from '@atlaskit/color-picker';
import { useIntl } from '@atlassian/jira-intl';
import type { ColorCardVariant, ColorPaletteVariant } from '../types.tsx';
import {
	CHECK_MARK_COLOR_DARK,
	CHECK_MARK_COLOR_LIGHT,
	OPTION_COLOR_THEMES_COLORFUL_SINGLE_SELECT,
	OPTION_COLOR_THEMES_COLORFUL_DARKER_SINGLE_SELECT,
	OPTION_COLOR_THEMES,
} from './constants.tsx';
import { getColorNameFromValue, getValueFromColorName } from './utils/index.tsx';

type Props = {
	color: string;
	onChangeColor: (color: string) => void;
	colorPaletteVariant?: ColorPaletteVariant;
	colorCardVariant?: ColorCardVariant;
};

export const ColorPickerMenu = ({
	color,
	onChangeColor,
	colorPaletteVariant = 'old',
	colorCardVariant = 'fill',
}: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const getColorPalette = () => {
		switch (colorPaletteVariant) {
			case 'new':
				return OPTION_COLOR_THEMES_COLORFUL_SINGLE_SELECT;
			case 'newDarker':
				return OPTION_COLOR_THEMES_COLORFUL_DARKER_SINGLE_SELECT;
			case 'old':
			default:
				return OPTION_COLOR_THEMES;
		}
	};

	const colorPalette = getColorPalette().map(({ backgroundColor, borderColor, messageLabel }) => ({
		label: formatMessage(messageLabel),
		value: colorCardVariant === 'outline' ? (borderColor ?? backgroundColor) : backgroundColor,
	}));

	const handleChangeColor = useCallback(
		(e: MouseEvent | KeyboardEvent, newColor: string, variant: ColorCardVariant) => {
			onChangeColor(getColorNameFromValue(newColor, variant));
			e.stopPropagation();
		},
		[onChangeColor],
	);

	return (
		<ColorPaletteMenu
			cols={colorPaletteVariant === 'old' ? colorPalette.length / 5 : colorPalette.length}
			palette={colorPalette}
			selectedColor={getValueFromColorName(color, colorCardVariant)}
			onChange={(e: MouseEvent | KeyboardEvent, newColor: string) =>
				handleChangeColor(e, newColor, colorCardVariant)
			}
			mode={Compact}
			checkMarkColor={color.includes('LIGHT') ? CHECK_MARK_COLOR_LIGHT : CHECK_MARK_COLOR_DARK}
			autoFocus
			isFocusLockEnabled={false}
			isInsideMenu={false}
			variant={colorCardVariant}
		/>
	);
};
